// components/ui/stats-card.tsx
import * as React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card"
import { cn } from "lib/utils"

type CardVariant = "total" | "positive" | "negative" | "neutral"

export interface StatsCardProps {
  title: string
  /** number of customers */
  count: number
  /** CM value (number will be formatted as INR compact, string shown as-is) */
  cm: number | string
  /** CM% (accepts "12.3%" or 12.3) */
  cmPct: number | string
  /** If true, forces the "total" gradient */
  isTotal?: boolean
  /** Optional click handler to make the card interactive */
  onClick?: () => void
  /** Extra classNames */
  className?: string
  /** Optional footer content (small note, hint, etc.) */
  footer?: React.ReactNode
}

function parsePct(v: number | string): number {
  if (typeof v === "number") return v
  const n = Number(String(v).replace("%", "").trim())
  return Number.isFinite(n) ? n : 0
}

function pctString(v: number | string): string {
  return typeof v === "string" ? v : `${v.toFixed(1)}%`
}

function formatCount(n: number): string {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)
}

function formatINR(v: number | string): string {
  if (typeof v === "string") return v
  // Format with Indian number system (lakhs, crores)
  const absValue = Math.abs(v)
  const isNegative = v < 0
  const prefix = isNegative ? "-" : ""
  
  if (absValue >= 10000000) { // 1 crore
    const crores = absValue / 10000000
    return `${prefix}${crores.toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`
  } else if (absValue >= 100000) { // 1 lakh
    const lakhs = absValue / 100000
    return `${prefix}${lakhs.toLocaleString('en-IN', { maximumFractionDigits: 2 })} L`
  } else {
    return `${prefix}${absValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
  }
}

function pickVariant(isTotal: boolean | undefined, pct: number): CardVariant {
  if (isTotal) return "total"
  if (pct > 0) return "positive"
  if (pct < 0) return "negative"
  return "neutral"
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  count,
  cm,
  cmPct,
  isTotal,
  onClick,
  className,
  footer,
}) => {
  const pct = parsePct(cmPct)
  const variant = pickVariant(isTotal, pct)
  const positive = pct >= 0

  return (
    <Card
      variant={variant}
      onClick={onClick}
      className={cn(
        "overflow-hidden cursor-default select-none flex flex-col",
        onClick && "cursor-pointer transition-transform hover:scale-[1.01] active:scale-[0.99]",
        className
      )}
    >
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-sm font-medium text-slate-800">{title}</CardTitle>
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold tabular-nums",
              positive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
            )}
            title="Contribution Margin %"
          >
            {pctString(typeof cmPct === "number" ? Number(cmPct) : pct)}
          </span>
        </div>
      </CardHeader>

      <CardContent className="pt-0 flex-grow flex flex-col justify-end">
        <div className="space-y-3 mt-auto">
          {/* Count */}
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold tabular-nums text-slate-900">
              {formatCount(Number(count))}
            </span>
          </div>

          {/* CM - always at bottom */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">CM (INR)</span>
            <span className={cn("font-semibold", isTotal ? "text-blue-900" : "text-slate-900")}>
              {formatINR(cm)}
            </span>
          </div>
        </div>

        {footer ? (
          <div className="mt-2 text-xs text-slate-600/80">{footer}</div>
        ) : null}
      </CardContent>
    </Card>
  )
}
